import { useRef, useEffect, useState } from 'react';
import ScoringCeroPercentIcon from '../assets/img/scoringCeroPercent';
import LockBlueIcon from '../assets/img/lockBlueIcon';
import './ScoringCss.css';
import { Text4, Text4M } from '../genericComponents/texts';
import { BlankSpace } from '../genericComponents/layouts';
import Select from '../assets/img/Select.svg';
import SelloAf from '../assets/img/SelloAf.svg';
import { reportAutofactUrl } from '../config/config';
import Scoring from '../models/scoring.model';

interface ScoringData {
  scoring: Scoring;
}

interface ScoringPointAndSummaryVehiclesProps {
  scoringData: ScoringData;
}

export default function ScoringPointAndSummaryVehiclesComponent({ scoringData } : ScoringPointAndSummaryVehiclesProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const [pointStatus, setPointStatus] = useState('');
  let finalScore = 0;
  const reportInfoUrl = scoringData.scoring.reportCode ? reportAutofactUrl + scoringData.scoring.reportCode : '';

  if (Object.keys(scoringData).length > 0 && Object.keys(scoringData.scoring).length > 0) {
    finalScore = scoringData.scoring.finalScore ? scoringData.scoring.finalScore : 0;
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && canvas instanceof HTMLCanvasElement) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        setContext(ctx);
      } else {
        console.error('2D context not supported or canvas already initialized.');
      }
    } else {
      console.error('Element is not a canvas or is not rendered yet.');
    }
  }, []);

  useEffect(() => {
    if (context) {
      gaugeScore(finalScore);
    }
  }, [context, finalScore]);

  const gaugeScore = (scoringPoints: number) => {
    const degreesToRadians = (degrees:number) => 2 * Math.PI * (degrees / 360);

    // const minValue = 0;
    const maxValue = 100;

    const lineWidth = 9; // Este primer lineWidth es para el grosor de las 4 lineas de la semicircunferencia
    const gapBetweetSections = 2;
    const centerInX = 125;
    const centerInY = 130;
    const radius = 110;

    const colors = {
      red: {
        main: 'rgba(219, 41, 41,1)',
        light: 'rgba(219, 41, 41,0.4)',
      },
      orange: {
        main: 'rgba(255, 121, 76,1)',
        light: 'rgba(255, 121, 76,0.4)',
      },
      yellow: {
        main: 'rgba(255, 172, 26,1)',
        light: 'rgba(255, 172, 26,0.4)',
      },
      green: {
        main: 'rgba(56, 158, 13,1)',
        light: 'rgba(56, 158, 13,0.4)',
      },
    };

    const sections = [{
      startAt: 0,
      endAt: 50,
      activeColor: colors.red.main,
      inactiveColor: colors.red.light,
      pointStatus: 'Bajo',
    },
    {
      startAt: 51,
      endAt: 70,
      activeColor: colors.orange.main,
      inactiveColor: colors.orange.light,
      pointStatus: 'Regular',
    },
    {
      startAt: 71,
      endAt: 95,
      activeColor: colors.yellow.main,
      inactiveColor: colors.yellow.light,
      pointStatus: 'Bueno',
    },
    {
      startAt: 96,
      endAt: 100,
      activeColor: colors.green.main,
      inactiveColor: colors.green.light,
      pointStatus: 'Excelente',
    },
    ];

    let mainColorForActiveSection = '';

    sections.forEach((section, index) => {
      const isFirstSection = index === 0;
      const isLastSection = index === sections.length - 1;

      const sectionStartAt = isFirstSection ? section.startAt : section.startAt + gapBetweetSections;
      const sectionEndAt = isLastSection ? section.endAt : section.endAt - gapBetweetSections;

      const isActiveSection = scoringPoints >= section.startAt && scoringPoints <= section.endAt;
      const color = isActiveSection ? (setPointStatus(section.pointStatus), section.activeColor) : section.inactiveColor;

      if (isActiveSection) mainColorForActiveSection = section.activeColor;

      const startInDegrees = (sectionStartAt * 180) / maxValue + 180;
      const endInDegrees = (sectionEndAt * 180) / maxValue + 180;

      const startInRAD = degreesToRadians(startInDegrees);
      const endInRAD = degreesToRadians(endInDegrees);

      if (context) {
        context.beginPath();
        context.lineWidth = lineWidth;
        context.lineCap = 'round';

        //      arc( x ,  y , radius, startAngle, endAngle, counterClockwise);
        context.arc(centerInX, centerInY, radius, startInRAD, endInRAD);

        // set line color
        context.strokeStyle = color;
        context.stroke();
      }
    });

    if (context) {
      context.font = 'bold 45px Arial';
      context.textAlign = 'right';
      context.fillText(scoringPoints.toString(), 140, 130);
      context.font = '20px Arial';
      context.textAlign = 'left';
      context.fillText('/100', 140, 130);

      const finalScoreInDegrees = (scoringPoints * 180) / maxValue + 180;
      const finalScoreInRadians = degreesToRadians(finalScoreInDegrees);

      context.beginPath();
      context.lineWidth = 20; // Este lineWidth es para el grosor de la esfera pequena que constituye el punto en la semiesfera
      context.lineCap = 'round';
      context.arc(centerInX, centerInY, radius, finalScoreInRadians, finalScoreInRadians);
      context.strokeStyle = mainColorForActiveSection;
      context.stroke();

      context.beginPath();
      context.lineWidth = 10;
      context.lineCap = 'round';
      context.arc(centerInX, centerInY, radius, finalScoreInRadians, finalScoreInRadians);
      context.strokeStyle = '#FFF';
      context.stroke();

      /* eslint-disable-next-line no-plusplus */
      for (let index = 0, i = 0; i <= 14; index = ++i) {
        // Este for es para dibujar los punticos mas pequenos que rodean la semicircunferencia
        const dot = degreesToRadians(180 + (index * 13));
        context.beginPath();
        context.lineWidth = 2;
        context.lineCap = 'round';
        context.arc(centerInX, centerInY, radius - 25, dot, dot);
        context.strokeStyle = '#000';
        context.stroke();
      }
    }
  };

  return (
    <div className="ScoringPointVehicleContainer">
      <div className="header-img-counter-timer lg:w-full">
        <Text4M className="content">Puntaje Autofact según su información legal</Text4M>
        <BlankSpace height="24px" />
        { finalScore === 0
          ? (
            <>
              <div className="content">
                <ScoringCeroPercentIcon />
              </div>
              <div className="content">
                <LockBlueIcon color="#004CC6" />
                <p>Información no disponible</p>
              </div>
            </>
          ) : (
            <>
              <div className="scoring-section-available">
                <div className="gaugeContainer">
                  <canvas ref={canvasRef} id="gaugeScore" data-score={finalScore} />
                </div>
              </div>
              <Text4 className="content">
                Este vehículo tiene un puntaje&nbsp;
                <Text4M>{pointStatus}</Text4M>
              </Text4>
              <BlankSpace height="24px" />
              <Text4 className="content">
                Información exclusiva por la compra de pack
              </Text4>
              <BlankSpace height="5px" />
              <Text4M className="content">
                Informe Full + Informe Precio y Tasación.
              </Text4M>
              <div className="report-link-btn content">
                <a
                  href={reportInfoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button-link"
                >
                  <img src={Select} alt="Link" />
                  &nbsp;Ver Informe Historial
                </a>
              </div>
              <div className="content">
                <img src={SelloAf} alt="" />
              </div>
            </>
          )}
      </div>
    </div>
  );
}
